import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';

function createPetalGeometry(scale) {
  // Draw petal outline with curves — teardrop/heart shape
  const shape = new THREE.Shape();
  const s = scale;
  shape.moveTo(0, -s * 1.2);
  shape.bezierCurveTo(s * 0.8, -s * 0.6, s * 0.9, s * 0.4, 0, s * 1.2);
  shape.bezierCurveTo(-s * 0.9, s * 0.4, -s * 0.8, -s * 0.6, 0, -s * 1.2);

  const geometry = new THREE.ShapeGeometry(shape, 8);

  // Add curvature — bend petal like a real one
  const pos = geometry.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const curl = Math.pow(Math.abs(x) / (s * 0.9), 2) * s * 0.5;
    const fold = Math.sin((y / (s * 1.2)) * Math.PI * 0.5) * s * 0.3;
    pos.setZ(i, curl + fold);
  }
  pos.needsUpdate = true;
  geometry.computeVertexNormals();

  return geometry;
}

const RosePetals = () => {
  const mountRef = useRef(null);

  useEffect(() => {
    const container = mountRef.current;
    if (!container) return;

    const width = container.clientWidth;
    const height = container.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
    camera.position.z = 50;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);

    const petalCount = 18;
    const petals = [];

    const colors = [
      new THREE.Color('#FFD6E0'),
      new THREE.Color('#FFC8DD'),
      new THREE.Color('#FFE0EB'),
      new THREE.Color('#FFEBF0'),
      new THREE.Color('#FFF0F5'),
    ];

    for (let i = 0; i < petalCount; i++) {
      const scale = Math.random() * 1.0 + 0.8;
      const geometry = createPetalGeometry(scale);

      const color = colors[Math.floor(Math.random() * colors.length)];
      const material = new THREE.MeshBasicMaterial({
        color: color,
        transparent: true,
        opacity: Math.random() * 0.2 + 0.55,
        side: THREE.DoubleSide,
        depthWrite: false,
      });

      const mesh = new THREE.Mesh(geometry, material);

      // Spread across the visible area (camera FOV ~37 units at z=0)
      mesh.position.x = (Math.random() - 0.5) * 80;
      mesh.position.y = (Math.random() - 0.3) * 60;
      mesh.position.z = (Math.random() - 0.5) * 20;

      mesh.rotation.x = Math.random() * Math.PI;
      mesh.rotation.y = Math.random() * Math.PI;
      mesh.rotation.z = Math.random() * Math.PI;

      scene.add(mesh);

      petals.push({
        mesh,
        speedY: Math.random() * 0.06 + 0.02,
        speedX: (Math.random() - 0.5) * 0.02,
        rotSpeedX: (Math.random() - 0.5) * 0.008,
        rotSpeedY: (Math.random() - 0.5) * 0.006,
        rotSpeedZ: (Math.random() - 0.5) * 0.012,
        swayPhase: Math.random() * Math.PI * 2,
        swaySpeed: Math.random() * 0.004 + 0.002,
        swayAmount: Math.random() * 0.2 + 0.08,
      });
    }

    let animationId;
    const animate = () => {
      animationId = requestAnimationFrame(animate);

      petals.forEach((p) => {
        p.mesh.position.y -= p.speedY;
        p.mesh.position.x += p.speedX + Math.sin(p.swayPhase) * p.swayAmount;
        p.swayPhase += p.swaySpeed;

        // Gentle tumbling
        p.mesh.rotation.x += p.rotSpeedX;
        p.mesh.rotation.y += p.rotSpeedY;
        p.mesh.rotation.z += p.rotSpeedZ;

        // Respawn at top when fallen below view
        if (p.mesh.position.y < -38) {
          p.mesh.position.y = Math.random() * 10 + 35;
          p.mesh.position.x = (Math.random() - 0.5) * 80;
        }
      });

      renderer.render(scene, camera);
    };

    animate();

    const handleResize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationId);
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      petals.forEach((p) => {
        p.mesh.geometry.dispose();
        p.mesh.material.dispose();
      });
      renderer.dispose();
    };
  }, []);

  return (
    <div
      ref={mountRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1,
        pointerEvents: 'none',
      }}
    />
  );
};

export default RosePetals;
